import React from "react";
import type { IFaqGroup, TFaqLang } from "@/src/features/faq/types";
import FaqAccordionItem from "@/src/features/faq/components/FaqAccordionItem";
import FaqGroupHeading from "@/src/features/faq/components/FaqGroupHeading";

interface IFaqCategoryGroupProps {
  group: IFaqGroup;
  lang: TFaqLang;
  /** Distinguishes panel ids when two surfaces render the same category. */
  surface: "home" | "listing";
  open: boolean;
  onToggle: () => void;
  openItemId: string | null;
  onToggleItem: (id: string) => void;
}

/**
 * One collapsible FAQ category for the home and listing teasers: the category
 * heading, and every question under it revealed on expand.
 *
 * Collapsed is the resting state — a teaser should read as a short menu of
 * topics, not as the FAQ page inlined. The panel animates through
 * `grid-rows-[0fr] → [1fr]` (the same mechanism `/faq` uses) rather than
 * unmounting, so the questions stay in the DOM and crawlable either way.
 */
const FaqCategoryGroup: React.FC<IFaqCategoryGroupProps> = ({
  group,
  lang,
  surface,
  open,
  onToggle,
  openItemId,
  onToggleItem,
}) => {
  const panelId = `faq-${surface}-list-${group.id}`;

  return (
    <div>
      <FaqGroupHeading
        as="h3"
        label={group.label[lang]}
        count={group.items.length}
        open={open}
        onToggle={onToggle}
        controls={panelId}
      />

      <div
        className={`faq-panel-group grid ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <ul
            id={panelId}
            className={`faq-answer m-0 flex list-none flex-col gap-2.5 p-0 pt-2.5 md:gap-3 md:pt-3 ${
              open ? "opacity-100" : "opacity-0"
            }`}
          >
            {group.items.map((item, index) => (
              <FaqAccordionItem
                key={item.id}
                item={item}
                lang={lang}
                index={index}
                variant={surface}
                headingLevel="h4"
                showNumber
                isOpen={openItemId === item.id}
                onToggle={() => onToggleItem(item.id)}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FaqCategoryGroup;
